import {
  BitkitBadge,
  BitkitCheckbox,
  BitkitControlButton,
  BitkitTag,
  IconAndroid,
  IconApple,
  IconArrowNortheast,
  IconDetails,
  IconLaptop,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { useDisclosure } from '@chakra-ui/react/hooks';
import { Table } from '@chakra-ui/react/table';
import { Text } from '@chakra-ui/react/text';
import { useContext } from 'react';

import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { BuildCacheToolIds } from '@/models/BuildCacheTool';
import { BuildToolInvocationWithDetails } from '@/models/BuildToolInvocation';
import { isLocalBuildEnvironment } from '@/models/CiProvider';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import InvocationDetailsDrawer from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationDetailsDrawer';
import { useInvocationListTableContext } from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationListTableContext';
import InvocationStatusIcon from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationStatusIcon';
import { formatDateTime } from '@/utils/commonFormats';
import { buildToolInvocationPath } from '@/utils/monolithApiRouteService';

interface InvocationListItemMobileProps {
  invocation: BuildToolInvocationWithDetails;
}

const InvocationListItemMobile = ({ invocation }: InvocationListItemMobileProps) => {
  const { open, onOpen, onClose } = useDisclosure();

  const {
    account: { slug: workspaceSlug },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const {
    isBuildSpecific: isListBuildSpecific,
    isProjectSpecific: isListProjectSpecific,
    isInSelectMode,
    invocationsToCompare,
    setInvocationsToCompare,
  } = useInvocationListTableContext();
  const hideProjectInfo = isListBuildSpecific || isListProjectSpecific;
  const isLocal = isLocalBuildEnvironment(invocation.ciProvider);

  const selectedToCompare = invocationsToCompare.some(
    (anInvocation) => anInvocation.invocationId === invocation.invocationId,
  );

  const compareCheckboxLabel = `Select ${invocation.shortCommand || invocation.command} started at ${formatDateTime(
    invocation.startedAt,
    { hideSeconds: true, hideYear: true },
  )} for comparison`;

  const handleSelectToCompare = (isSelected: boolean) => {
    if (setInvocationsToCompare) {
      if (isSelected) {
        setInvocationsToCompare([...invocationsToCompare, invocation]);
      } else {
        setInvocationsToCompare(
          invocationsToCompare.filter((anInvocation) => anInvocation.invocationId !== invocation.invocationId),
        );
      }
    }
  };

  return (
    <>
      <Table.ScrollArea>
        <Table.Root>
          <Table.Header>
            <Table.Row cursor="pointer" onClick={onOpen}>
              <Table.ColumnHeader maxWidth="0">
                <Box alignItems="center" display="flex" gap=".5rem">
                  {isInSelectMode && setInvocationsToCompare && (
                    <Box onClick={(e) => e.stopPropagation()}>
                      <BitkitCheckbox
                        checked={selectedToCompare}
                        aria-label={compareCheckboxLabel}
                        onCheckedChange={({ checked }) => handleSelectToCompare(checked === true)}
                        state={!selectedToCompare && invocationsToCompare.length > 1 ? 'disabled' : undefined}
                      />
                    </Box>
                  )}
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap="1rem">
                    <Box display="flex" alignItems="center" gap=".5rem">
                      <InvocationStatusIcon status={invocation.status} size="16" />
                      <Text fontWeight="normal" truncate title={invocation.projectTitle}>
                        {invocation.projectTitle}
                      </Text>
                    </Box>
                    <Box>
                      {!isListBuildSpecific && (
                        <BitkitControlButton
                          label="Invocation details"
                          icon={IconDetails}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpen();
                          }}
                          tooltipProps={{ disabled: true }}
                        />
                      )}
                      <BitkitControlButton
                        label="Go to invocation details page"
                        href={buildToolInvocationPath(workspaceSlug, invocation.invocationId, invocation.tool)}
                        icon={IconArrowNortheast}
                      />
                    </Box>
                  </Box>
                </Box>
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>
                <Box display="flex" flexDirection="column" gap=".5rem" paddingY=".5rem">
                  {isLocal && (
                    <BitkitBadge colorVariant="blue" icon={IconLaptop} width="fit-content">
                      Build environment: local
                    </BitkitBadge>
                  )}
                  {!hideProjectInfo && (
                    <Box>
                      <Text as="span" fontWeight="bold">
                        Project:{' '}
                      </Text>
                      <Text as="span">{invocation.projectTitle}</Text>
                    </Box>
                  )}
                  <>
                    <Text as="span" fontWeight="bold">
                      Command:{' '}
                    </Text>
                    <Text as="span">{invocation.shortCommand || invocation.command}</Text>
                  </>
                  {invocation.tool === BuildCacheToolIds.reactNative &&
                    invocation.targetOs &&
                    invocation.targetOs.length > 0 && (
                      <Box>
                        <Text as="span" fontWeight="bold">
                          Target:{' '}
                        </Text>
                        <Box display="inline-flex" gap="8" flexWrap="wrap">
                          {invocation.targetOs.map((os) => (
                            <BitkitTag
                              key={os}
                              icon={os === 'ios' ? IconApple : IconAndroid}
                              labelText={os === 'ios' ? 'iOS' : 'Android'}
                              size="sm"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  {!isListBuildSpecific && (
                    <Box>
                      <Text as="span" fontWeight="bold">
                        Commit:{' '}
                      </Text>
                      <Text as="span">{invocation.commitHash}</Text>
                    </Box>
                  )}
                </Box>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table.Root>
      </Table.ScrollArea>
      <InvocationDetailsDrawer invocation={invocation} isOpen={open} onClose={onClose} />
    </>
  );
};

export default InvocationListItemMobile;
